import removeRedactedContentfulArray from 'utils/removeRedactedContentfulArray'

export const PROGRAM_DIGITAL_WELL_BEING = 'digital well-being unit'

// Used when Contentful redacts grade tab metadata but DWB is enabled.
const REQUIRED_DWB_GRADE_TAB_FALLBACKS = [
  {
    displayTitle: 'Grades K-5',
    route: 'grades-k-5',
  },
  {
    displayTitle: 'Grades 6-8',
    route: 'grades-6-8',
  },
  {
    displayTitle: 'Grades 9-12',
    route: 'grades-9-12',
  },
]

export const isDigitalWellBeingDashboardTile = internalTitle =>
  String(internalTitle || '')
    .toLowerCase()
    .trim() === PROGRAM_DIGITAL_WELL_BEING

export const shouldShowDashboardProgram = ({ hasDwbEnabledSite, program }) => {
  if (!isDigitalWellBeingDashboardTile(program?.internalTitle)) return true

  return hasDwbEnabledSite
}

const getAvailableProgramTiles = (programTiles, hasDwbEnabledSite) =>
  removeRedactedContentfulArray(programTiles).filter(program =>
    shouldShowDashboardProgram({ hasDwbEnabledSite, program }),
  )

const unredactDwbTile = dwbTile => {
  const withoutRedaction = entry => {
    if (!entry || typeof entry !== 'object') return entry

    const entryWithoutRedaction = { ...entry }
    delete entryWithoutRedaction.redacted

    return entryWithoutRedaction
  }

  return {
    ...withoutRedaction(dwbTile),
    staticLinks: dwbTile?.staticLinks?.map(withoutRedaction),
    teachLinks: dwbTile?.teachLinks?.map(withoutRedaction),
    trainingLinks: dwbTile?.trainingLinks?.map(withoutRedaction),
  }
}

const getDwbFallbackTile = programGroup => {
  const dwbTiles = programGroup.flatMap(group =>
    (group?.programTiles || [])
      .filter(program =>
        isDigitalWellBeingDashboardTile(program?.internalTitle),
      )
      .map(unredactDwbTile),
  )

  return (
    dwbTiles.find(dwbTile => dwbTile?.staticLinks?.length > 0) || dwbTiles[0]
  )
}

const withDwbTile = (group, dwbTile) => {
  const programTiles = group.programTiles || []
  const currentDwbTile = programTiles.find(program =>
    isDigitalWellBeingDashboardTile(program?.internalTitle),
  )
  const currentDwbTileHasLinks = currentDwbTile?.staticLinks?.length > 0

  if (currentDwbTileHasLinks) return group

  return {
    ...group,
    programTiles: [
      dwbTile,
      ...programTiles.filter(
        program => !isDigitalWellBeingDashboardTile(program?.internalTitle),
      ),
    ],
  }
}

const ensureDwbGradeTabs = (availableProgramGroups, dwbTile) =>
  REQUIRED_DWB_GRADE_TAB_FALLBACKS.reduce((groups, gradeTab) => {
    const existingGroupIndex = groups.findIndex(
      group => group.route === gradeTab.route,
    )

    if (existingGroupIndex >= 0) {
      return groups.map((group, index) =>
        index === existingGroupIndex ? withDwbTile(group, dwbTile) : group,
      )
    }

    return [
      ...groups,
      {
        ...gradeTab,
        programTiles: [dwbTile],
      },
    ]
  }, availableProgramGroups)

export const getAvailableProgramGroups = ({
  hasDwbEnabledSite,
  programGroup,
}) => {
  if (!Array.isArray(programGroup)) {
    return []
  }

  const availableGroups = programGroup.reduce((availableGroups, group) => {
    if (!group) return availableGroups

    if (!group.redacted) {
      const availableProgramTiles = getAvailableProgramTiles(
        group.programTiles,
        hasDwbEnabledSite,
      )
      if (availableProgramTiles.length === 0) return availableGroups

      return [
        ...availableGroups,
        {
          ...group,
          programTiles: availableProgramTiles,
        },
      ]
    }

    return availableGroups
  }, [])

  if (!hasDwbEnabledSite) {
    return availableGroups
  }

  const dwbTile = getDwbFallbackTile(programGroup)

  if (!dwbTile) {
    return availableGroups
  }

  return ensureDwbGradeTabs(availableGroups, dwbTile)
}

// hasDwbEnabledSite: any site with dwbp_enabled (includes K5+DWB combo) — used for DWB tiles.
// hasDwbOnlySite: standalone DWB only — may skip K8/HS LMS wait; see skipLmsPrerequisites.
export const checkReadyToLoad = ({ props }) => {
  const {
    activeDigitalLicenses,
    hasDwbEnabledSite,
    hasDwbOnlySite,
    instructorData,
    isFetchingInstructorData,
    isFetchingInstructorDataV2,
    isFetchingSetupLicenses,
    isFetchingActiveDigitalLicenses,
    isRefetching,
  } = props

  const licensesOrContentPending =
    isFetchingActiveDigitalLicenses ||
    isFetchingSetupLicenses ||
    isRefetching ||
    !Array.isArray(activeDigitalLicenses)

  const instructorFetchComplete =
    !isFetchingInstructorData && !isFetchingInstructorDataV2

  const isMissingLmsProgramData =
    !instructorData?.Classes ||
    !instructorData?.MiddleSchool ||
    !instructorData?.Sela ||
    !instructorData?.HighSchool

  const skipLmsPrerequisites =
    hasDwbOnlySite ||
    // DWB-only sites never receive K8/HS instructor payloads; don't block on them.
    (hasDwbEnabledSite && instructorFetchComplete && isMissingLmsProgramData)

  if (skipLmsPrerequisites) {
    return licensesOrContentPending
  }

  return isMissingLmsProgramData || licensesOrContentPending
}

export const formatTabName = tabName => {
  if (!tabName) return ''

  if (tabName === 'sel-for-adults') {
    // Second function logic
    return tabName
      .split('-') // Split by hyphen
      .map(word => {
        if (/^\d+$/.test(word)) {
          // If the word is a number, return it as is
          return word
        }
        // Otherwise, capitalize the first letter
        return word.charAt(0).toUpperCase() + word.slice(1)
      })
      .join(' ') // Join with spaces
      .replace(/(\d+) (\d+)/g, '$1-$2') // Reintroduce hyphens between numbers
  } else {
    // First function logic
    return tabName
      .replace('-', ' ') // Replace the first hyphen with a space
      .split(' ') // Split by spaces
      .map(word => {
        if (/^\d+$/.test(word)) {
          // If the word is a number, return it as is
          return word
        }
        // Otherwise, capitalize the first letter
        return word.charAt(0).toUpperCase() + word.slice(1)
      })
      .join(' ') // Join with spaces
  }
}
